// frontend/src/context/BillingContext.js

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { httpService } from "../../services/httpService";
import { toast } from "react-toastify";

const BillingCtx = createContext(null);

const EMPTY_CATEGORIES = {
  Consultation: [],
  Procedures: [],
  Medications: [],
  "Lab Tests": [],
};

const roundToTwo = (num) => {
  if (num === null || num === undefined || isNaN(num)) return 0;
  return Math.round((parseFloat(num) + Number.EPSILON) * 100) / 100;
};

const CATEGORY_TO_KEY = {
  Medications: "medications",
  Procedures: "procedures",
  "Lab Tests": "labTests",
};

/** Prefer treatment-plan lines; drop order/manual duplicates of the same product. */
const dedupeMedicationsAgainstTreatmentPlan = (medications = []) => {
  const planIds = new Set(
    medications
      .filter((i) => i.source === "treatment_plan" || i.source === "plan")
      .map((i) => String(i.product_id || i.catalogId || "").trim())
      .filter(Boolean),
  );
  if (planIds.size === 0) return medications;
  return medications.filter((i) => {
    if (i.source === "treatment_plan" || i.source === "plan") return true;
    const pid = String(i.product_id || i.catalogId || "").trim();
    return !(pid && planIds.has(pid));
  });
};

export const BillingProvider = ({ children, refId }) => {
  const [billingItems, setBillingItems] = useState(EMPTY_CATEGORIES);
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [manualItems, setManualItems] = useState([]);
  const [modifiedItems, setModifiedItems] = useState([]);
  const [billingVersion, setBillingVersion] = useState(1);
  const [excludedItems, setExcludedItems] = useState({ medications: [], procedures: [], labTests: [] });
  const [excludedItemsDetails, setExcludedItemsDetails] = useState({
    medications: [],
    procedures: [],
    labTests: [],
  });

  const saveTimeoutRef = useRef(null);
  const isDirtyRef = useRef(false);
  const isSavingRef = useRef(false);

  // NEW — refs that always hold the LATEST values, updated every render
  const manualItemsRef = useRef(manualItems);
  const modifiedItemsRef = useRef(modifiedItems);
  const paymentsRef = useRef(payments);
  const billingVersionRef = useRef(billingVersion);

  // NEW — keep refs in sync on every render (cheap, no extra effect dependency issues)
  manualItemsRef.current = manualItems;
  modifiedItemsRef.current = modifiedItems;
  paymentsRef.current = payments;
  billingVersionRef.current = billingVersion;

  // ─── FETCH BILLING DATA ──────────────────────────────────────────────────
  const fetchBillingData = useCallback(async (force = false) => {
    if (!refId) return;
    if (isDirtyRef.current && !force) return;

    setLoading(true);
    setLoadError(null);
    try {
      const response = await httpService.get(`/vendorpet/getbilling/${refId}`);

      if (response.code === 200) {
        const data = response.data;
        const metadata = data.billingMetadata || {};

        setBillingItems({
          Consultation: data.items?.consultation || [],
          Procedures: data.items?.procedures || [],
          Medications: dedupeMedicationsAgainstTreatmentPlan(
            data.items?.medications || [],
          ),
          "Lab Tests": data.items?.labTests || [],
        });

        setManualItems(metadata.manualItems || []);
        setModifiedItems(metadata.modifiedItems || []);
        setBillingVersion(metadata.version || 1);
        setExcludedItems(metadata.excludedItems || { medications: [], procedures: [], labTests: [] });
        setExcludedItemsDetails(metadata.excludedItemsDetails || {
          medications: [],
          procedures: [],
          labTests: [],
        });
        setPayments(data.payments || []);
        setSummary(data.summary || null); 

        isDirtyRef.current = false;
      }
    } catch (err) {
      console.error("Failed to fetch billing data:", err);
      setLoadError("Failed to load billing data");
    } finally {
      setLoading(false);
    }
  }, [refId]);

  // ─── BULK SAVE ──────────────────────────────────────────────────────────
  const saveBillingData = useCallback(async (force = false) => {
    if (!refId) return;
    if (!isDirtyRef.current && !force) return;
    if (isSavingRef.current) return;

    isSavingRef.current = true;
    setSaving(true);

    try {
      const payload = {
        manualItems: manualItemsRef.current,
        modifiedItems: modifiedItemsRef.current,
        payments: paymentsRef.current,
        version: billingVersionRef.current,
      };

      const response = await httpService.post(`/vendorpet/savebilling/${refId}`, payload);

      if (response.code === 200) {
        if (response.data?.version) {
          setBillingVersion(response.data.version);
        }
        isDirtyRef.current = false;
        await fetchBillingData();
        return true;
      } else {
        throw new Error(response.message || "Failed to save billing");
      }
    } catch (err) {
      console.error("🟨 [saveBillingData] FAILED:", err);
      toast.error(err.response?.data?.message || "Failed to save billing data");
      return false;
    } finally {
      setSaving(false);
      isSavingRef.current = false;
    }
  }, [refId, fetchBillingData]);

  const scheduleSave = useCallback(() => {
    clearTimeout(saveTimeoutRef.current);
    isDirtyRef.current = true;
    saveTimeoutRef.current = setTimeout(() => {
      saveBillingData();
    }, 1500);
  }, [saveBillingData]);

  /** Flush pending debounce and save immediately (used on Mark Complete). */
  const flushSave = useCallback(async () => {
    clearTimeout(saveTimeoutRef.current);
    return saveBillingData(false);
  }, [saveBillingData]);

  // ─── REMOVE ITEM ────────────────────────────────────────────────────────
  const removeItem = useCallback(async (category, itemId) => {
    const categoryKey = CATEGORY_TO_KEY[category];
    const item = (billingItems[category] || []).find((i) => i.id === itemId);
    if (!item) return;

    setBillingItems((prev) => ({
      ...prev,
      [category]: prev[category].filter((i) => i.id !== itemId),
    }));

    if (item.source === "manual") {
      setManualItems((prev) => prev.filter((m) => m.id !== itemId));
      scheduleSave();
      toast.success(`"${item.name}" removed from billing`);
    } else {
      try {
        await httpService.post(`/vendorpet/savebilling/${refId}`, {
          action: "exclude",
          category: categoryKey,
          itemId,
        });
        toast.success(`"${item.name}" removed from billing`);
        await fetchBillingData(true);
      } catch (err) {
        console.error("Failed to exclude item:", err);
        toast.error("Failed to remove item — refreshing");
        fetchBillingData(true);
      }
    }

    return item;
  }, [billingItems, refId, scheduleSave, fetchBillingData]);

  // ─── RESTORE ITEM ──────────────────────────────────────────────────────
  const restoreItem = useCallback(async (category, itemId) => {
    const categoryKey = CATEGORY_TO_KEY[category];
    try {
      await httpService.post(`/vendorpet/savebilling/${refId}`, {
        action: "restore",
        category: categoryKey,
        itemId,
      });
      toast.success("Item restored to billing");
      await fetchBillingData(true);
    } catch (err) {
      console.error("Failed to restore item:", err);
      toast.error("Failed to restore item");
    }
  }, [refId, fetchBillingData]);

  const addManualItem = useCallback((category, item) => {
    const categoryKey = category === 'Medications' ? 'medication' :
                        category === 'Procedures' ? 'procedure' : 'lab_test';

    const productId = item.product_id || item.catalogId || item._id || null;

    const newItem = {
      id: productId
        ? String(productId)
        : `manual-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      name: item.name,
      price: item.price || 0,
      quantity: item.quantity || 1,
      discountPct: item.discountPct || 0,
      taxPct: item.taxPct || 0,
      category: categoryKey,
      source: 'manual',
      isManual: true,
      ...(productId ? { product_id: productId, catalogId: productId } : {}),
      sku_number: item.sku_number || "",
      unit: item.unit || "",
      brand: item.brand || "",
      batch_number: item.batch_number || "",
      mrp: item.mrp || 0,
      sale_price: item.sale_price || item.price || 0,
    };

    setManualItems(prev => {
      const next = [...prev, newItem];
      return next;
    });
    setBillingItems(prev => ({
      ...prev,
      [category]: [...prev[category], newItem]
    }));

    scheduleSave();
    return newItem;
  }, [scheduleSave]);

  const updateItem = useCallback((category, itemId, field, value) => {
    const categoryKey = CATEGORY_TO_KEY[category];

    const item = billingItems[category]?.find(i => i.id === itemId);
    if (!item) return;

    if (item.source === 'manual') {
      setManualItems(prev => prev.map(m =>
        m.id === itemId ? { ...m, [field]: value } : m
      ));
    }

    if (item.source !== 'manual') {
      setModifiedItems(prev => {
        const existingIndex = prev.findIndex(m => m.item_id === itemId);
        if (existingIndex !== -1) {
          const updated = [...prev];
          updated[existingIndex] = {
            ...updated[existingIndex],
            [`modified_${field}`]: value,
            modified_at: new Date(),
          };
          return updated;
        }

        return [
          ...prev,
          {
            item_id: itemId,
            source: categoryKey,
            name: item.name,
            original_quantity: item.quantity || 1,
            modified_quantity: field === 'quantity' ? value : (item.quantity || 1),
            original_price: item.price || 0,
            modified_price: field === 'price' ? value : (item.price || 0),
            modified_at: new Date(),
          }
        ];
      });
    }

    setBillingItems(prev => ({
      ...prev,
      [category]: prev[category].map(item =>
        item.id === itemId ? { ...item, [field]: value } : item
      )
    }));

    scheduleSave();
  }, [billingItems, scheduleSave]);

  const addPayment = useCallback((payment) => {
    const newPayment = {
      id: `pay-${Date.now()}`,
      amount: payment.amount || 0,
      method: payment.method || 'cash',
      note: payment.note || '',
      date: new Date(),
    };

    setPayments(prev => [...prev, newPayment]);
    scheduleSave();
    return newPayment;
  }, [scheduleSave]);

  const removePayment = useCallback((paymentId) => {
    setPayments(prev => prev.filter(p => p.id !== paymentId));
    scheduleSave();
  }, [scheduleSave]);

  const getBillingState = useCallback(() => ({
    billingItems,
    payments,
    manualItems,
    modifiedItems,
    version: billingVersion,
  }), [billingItems, payments, manualItems, modifiedItems, billingVersion]);

  // ⭐ CALCULATE TOTALS WITH ROUND OFF
  const calculateTotals = useCallback(() => {
    const allItems = [
      ...billingItems.Consultation.map(i => ({ ...i, __category: "Consultation" })),
      ...billingItems.Procedures.map(i => ({ ...i, __category: "Procedures" })),
      ...billingItems.Medications.map(i => ({ ...i, __category: "Medications" })),
      ...billingItems["Lab Tests"].map(i => ({ ...i, __category: "Lab Tests" })),
    ];

    let base = 0, discountAmt = 0, taxAmt = 0, lineTotal = 0;

    allItems.forEach(item => {
      const qty = item.quantity || 1;
      const price = item.price || 0;
      const taxPct = item.taxPct || 0;
      const priceIncludesTax = !!item.price_includes_tax;

      let itemAmountWithoutTax, itemTax, itemDiscount, itemTotal;

      if (priceIncludesTax) {
        const basePerUnit = taxPct > 0 ? price / (1 + taxPct / 100) : price;
        const taxPerUnit = price - basePerUnit;
        itemAmountWithoutTax = basePerUnit * qty;
        itemTax = taxPerUnit * qty;
        itemDiscount = 0;
        itemTotal = price * qty;
      } else {
        const discountPct = item.discountPct || 0;
        const itemBase = price * qty;
        itemDiscount = (itemBase * discountPct) / 100;
        const taxable = itemBase - itemDiscount;
        itemTax = (taxable * taxPct) / 100;
        itemAmountWithoutTax = taxable;
        itemTotal = itemAmountWithoutTax + itemTax;
      }

      base += itemAmountWithoutTax + itemDiscount;
      discountAmt += itemDiscount;
      taxAmt += itemTax;
      lineTotal += itemTotal;
    });

    const roundOff = Math.round(lineTotal) - lineTotal;
    const finalTotal = lineTotal + roundOff;

    const totalPaid = summary?.totalPaid ?? payments.reduce((sum, p) => sum + p.amount, 0);
    const balanceDue = Math.max(finalTotal - totalPaid, 0);

    return {
      base: Math.round(base * 100) / 100,
      discountAmt: Math.round(discountAmt * 100) / 100,
      taxAmt: Math.round(taxAmt * 100) / 100,
      lineTotal: Math.round(lineTotal * 100) / 100,
      roundOff: Math.round(roundOff * 100) / 100,
      finalTotal: Math.round(finalTotal * 100) / 100,
      totalPaid: Math.round(totalPaid * 100) / 100,
      balanceDue: Math.round(balanceDue * 100) / 100,
    };
  }, [billingItems, payments, summary]);

  const clearAll = useCallback(() => {
    setBillingItems(EMPTY_CATEGORIES);
    setManualItems([]);
    setModifiedItems([]);
    setPayments([]);
    isDirtyRef.current = true;
    scheduleSave();
  }, [scheduleSave]);

  const resetDirty = useCallback(() => {
    isDirtyRef.current = false;
    clearTimeout(saveTimeoutRef.current);
  }, []);

  useEffect(() => {
    if (refId) {
      fetchBillingData();
    }
  }, [refId, fetchBillingData]);

  useEffect(() => {
    return () => {
      clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  const value = {
    billingItems,
    setBillingItems,
    payments,
    summary,
    setPayments,
    loading,
    saving,
    loadError,
    manualItems,
    modifiedItems,
    billingVersion,
    excludedItems,
    excludedItemsDetails,
    fetchBillingData,
    saveBillingData,
    flushSave,
    removeItem,
    restoreItem,
    addManualItem,
    updateItem,
    addPayment,
    removePayment,
    calculateTotals,
    getBillingState,
    clearAll,
    resetDirty,
    isDirty: isDirtyRef.current,
  };

  return <BillingCtx.Provider value={value}>{children}</BillingCtx.Provider>;
};

export const useBilling = () => {
  const ctx = useContext(BillingCtx);
  if (!ctx) {
    throw new Error("useBilling must be used within a BillingProvider");
  }
  return ctx;
};